import { useRef, useState } from "react";
import { getOidcConfig } from "@/config/auth";

interface TokenInfo {
  token: string;
  expiresAt?: number;
}

const calculateExpiresAt = (expiresIn?: unknown) => {
  if (typeof expiresIn === "number") {
    return Date.now() + expiresIn * 1000;
  }

  if (typeof expiresIn === "string") {
    const parsed = Number.parseInt(expiresIn, 10);
    if (!Number.isNaN(parsed)) {
      return Date.now() + parsed * 1000;
    }
  }

  return undefined;
};

export const useOidcTokenRefresh = () => {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshResult, setLastRefreshResult] = useState<string>("");
  const [hasValidRefreshToken, setHasValidRefreshToken] = useState(false);
  const refreshPromiseRef = useRef<Promise<TokenInfo | null> | null>(null);

  const refreshAccessToken = async (): Promise<TokenInfo | null> => {
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    setIsRefreshing(true);

    const refreshPromise = (async () => {
      try {
        // doorkeeper標準の/oauth/tokenエンドポイントを使用
        const oidcConfig = getOidcConfig();
        const tokenEndpoint =
          oidcConfig.metadata?.token_endpoint ||
          `${import.meta.env.VITE_OIDC_AUTHORITY}/oauth/token`;

        // refresh_token grantのリクエスト
        const formData = new URLSearchParams();
        formData.append("grant_type", "refresh_token");
        formData.append("client_id", import.meta.env.VITE_OIDC_CLIENT_ID);
        // refresh_tokenはサーバーサイドでクッキーから自動的に設定される

        const response = await fetch(tokenEndpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: formData,
          credentials: "include", // HTTPオンリークッキー(__refresh_token)を含めて送信
        });

        if (response.ok) {
          const data = await response.json();
          const tokenInfo: TokenInfo = {
            token: data.access_token,
            expiresAt: calculateExpiresAt(data.expires_in),
          };

          setAccessToken(data.access_token);
          setHasValidRefreshToken(true);
          setLastRefreshResult("✅ Refresh Success");

          return tokenInfo;
        }

        // リフレッシュトークンが無効または期限切れの場合
        if (response.status === 401 || response.status === 400) {
          setAccessToken(null);
          setHasValidRefreshToken(false);
          setLastRefreshResult("❌ Refresh Token Invalid/Expired");
          console.log("❌ Refresh token is invalid or expired");
          return null;
        }

        const errorData = await response.json().catch(() => ({}));
        setAccessToken(null);
        setHasValidRefreshToken(false);
        setLastRefreshResult(`❌ Refresh Failed: ${response.status}`);
        console.log("Refresh failed:", errorData);
        return null;
      } catch (error) {
        console.error("Token refresh failed:", error);
        setAccessToken(null);
        setLastRefreshResult("❌ Refresh Network Error");
        return null;
      } finally {
        setIsRefreshing(false);
        refreshPromiseRef.current = null;
      }
    })();

    refreshPromiseRef.current = refreshPromise;
    return refreshPromise;
  };

  return {
    accessToken,
    refreshAccessToken,
    isRefreshing,
    lastRefreshResult,
    hasValidRefreshToken,
  };
};
